import { AxiosError } from 'axios';
import Icon from '@components/common/assets';
import Image from '@components/common/image';
import { useToast } from '@components/common/toast';
import Tooltip from '@components/common/tooltip';
import { PageState } from '@domain/rs/common/PageableRs';
import {
  MATCH_LIST_QUERY_KEY,
  useLikeFavoritePosition,
  useReadMatchingPosition,
  useUnlikeFavoritePosition,
} from '@repository/match';
import LikeButton from '@components/match/matchTab/matchList/LikeButton';
import MatchingStatusListRq from '@domain/rq/match/MatchingStatusListRq';
import MatchingStatusListVO, { MatchingStatusVO } from '@domain/vo/match/MatchingStatusListVO';
import { MatchingStatusDto } from '@domain/rs/match/MatchingStatusListRs';
import Colors from '@domain/constant/colors';
import useMessageModal from '@utils/hooks/useMessageModal';
import { V2Route } from '@utils/routeUtil/V2Route';
import { queryClient } from '@utils/queryClient';

import { RouteUrl } from '@utils/routeUtil';
import classnames from 'classnames/bind';
import styles from './matchListCard.module.scss';
const cx = classnames.bind(styles);

interface MatchStatusProps extends Pick<MatchingStatusDto, 'statusType' | 'responseType'> {
  onOpenMessage: () => void;
  positionSn: number;
  isOfferClosed: boolean;
}

const MatchStatus = ({ statusType, responseType, onOpenMessage, positionSn, isOfferClosed }: MatchStatusProps) => {
  if (isOfferClosed) {
    return '제안 마감';
  }

  switch (statusType) {
    case 'RECOMMEND':
      return '추천됨';
    case 'PROFILE_OPEN':
      return '프로필 열람';
    case 'OFFER':
      switch (responseType) {
        case 'ACCEPTED': {
          const query = new URLSearchParams({ sn: positionSn.toString() }).toString();
          return (
            <a className={cx('accepted')} href={`${RouteUrl.MY_PAGE_APPLY}?${query}`} target="_blank" rel="noreferrer">
              수락 완료
              <div className={cx('arrowRight')}>
                <Icon name="arrowRightLight" width={16} height={16} fill={Colors.C_WHITE} />
              </div>
            </a>
          );
        }
        case 'REJECTED':
          return <div className={cx('rejected')}>제안 거절</div>;
        case 'UNANSWERED':
          return '제안 미응답';
        default:
          return (
            <a role="button" className={cx('proposed')} onClick={onOpenMessage}>
              제안내용 확인
            </a>
          );
      }
    case 'OFFER_CLOSED':
      return '제안 마감';
    default:
      return null;
  }
};

const THREE_SECONDS = 3000;

type MatchingListQueryType = {
  pages: {
    matchingList: MatchingStatusListVO;
    pages: PageState;
  }[];
};

interface Props extends MatchingStatusVO {
  pageParams: MatchingStatusListRq;
  firstRecommendOrProfileOpenMatchingSn?: number;
  isFirstTooltipVisible: boolean;
}

const MatchListCard = ({
  companySn,
  companyName,
  companyLogoUrl,
  positionSn,
  positionName,
  statusType,
  responseType,
  isNeedBadge,
  matchingStatusLastModifiedDateText,
  readYn,
  matchingSn,
  likeYn,
  pageParams,
  firstRecommendOrProfileOpenMatchingSn,
  isFirstTooltipVisible,
  dueDateDDay,
  decisionDueDateDDayText,
  isOfferClosed,
}: Props) => {
  const isRejected = responseType === 'REJECTED';
  const isUnanswered = responseType === 'UNANSWERED';
  const isClosed = isOfferClosed || isRejected || isUnanswered;
  const recommendButtonVisible = statusType === 'RECOMMEND';
  const Toast = useToast();

  // 탭 전환 && 현재 Item이 열람 or 관심 중 관심기업 비활성화 첫 번째 요소일 경우 마운트됐을 때 3초간 툴팁 보여주기
  const durationForFirstRender =
    isFirstTooltipVisible && firstRecommendOrProfileOpenMatchingSn === matchingSn ? THREE_SECONDS : undefined;

  const onMutate = async () => {
    await queryClient.cancelQueries([MATCH_LIST_QUERY_KEY, pageParams]);
    const previousData = queryClient.getQueryData<MatchingListQueryType>([MATCH_LIST_QUERY_KEY, pageParams]);

    queryClient.setQueryData([MATCH_LIST_QUERY_KEY, pageParams], () => {
      return {
        ...previousData,
        pages: [
          {
            ...previousData?.pages[0],
            matchingList: {
              ...previousData?.pages[0].matchingList,
              matchingList: previousData?.pages[0].matchingList.matchingList.map((item) => {
                if (item.matchingSn === matchingSn) {
                  return {
                    ...item,
                    likeYn: !item.likeYn,
                  };
                }
                return item;
              }),
            },
          },
        ],
      };
    });

    return { previousData };
  };

  const onError = (_error: AxiosError, _newData: number, context: unknown) => {
    queryClient.setQueryData(
      [MATCH_LIST_QUERY_KEY, pageParams],
      (context as { previousData: MatchingListQueryType })?.previousData,
    );
    Toast({ iconType: 'info', type: 'error', content: '오류가 발생했어요. 다시 시도해주세요.' });
  };

  const onSettled = () => {
    queryClient.invalidateQueries([MATCH_LIST_QUERY_KEY, pageParams]);
  };

  const { mutate: readMatchingPosition } = useReadMatchingPosition();
  const { mutateAsync: addCompanyLikeAsync, isLoading: isLoadingAddCompanyLike } = useLikeFavoritePosition({
    onMutate,
    onError,
    onSettled,
    onSuccess: () => {
      Toast({ iconType: 'info', content: '인사담당자에게 관심 표현이 전달되었어요.' });
    },
  });
  const { mutateAsync: deleteCompanyLikeAsync, isLoading: isLoadingDeleteCompanyLike } = useUnlikeFavoritePosition({
    onMutate,
    onError,
    onSettled,
    onSuccess: () => {
      Toast({ iconType: 'info', content: '관심 표현을 취소했어요.' });
    },
  });
  const { openMessageModal } = useMessageModal();

  const onOpenMessage = async () => {
    await openMessageModal(positionSn);
    !readYn && readMatchingPosition(matchingSn);
  };

  const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (isClosed) {
      event.preventDefault();
    }
  };

  const handleClickLikeButton = async () => {
    if (isLoadingAddCompanyLike || isLoadingDeleteCompanyLike || !positionSn) return;

    if (likeYn) {
      await deleteCompanyLikeAsync(positionSn);
    } else {
      await addCompanyLikeAsync(positionSn);
    }
  };

  return (
    <div className={cx('container', { closed: isClosed })}>
      <a
        className={cx('image')}
        href={V2Route.getCompanyChannelPage(companySn)}
        target="_blank"
        rel="noreferrer"
        onClick={handleLinkClick}
      >
        {companyLogoUrl ? (
          <Image width={48} height={48} src={companyLogoUrl} alt={companyName} />
        ) : (
          <Icon name="logo" width={32} height={7} fill={Colors.C_COOL_GRAY_50} />
        )}
      </a>

      <div className={cx('infoContainer')}>
        <div className={cx('infoTitleContainer')}>
          <a
            className={cx('infoTitle')}
            href={V2Route.getPositionJDPage(positionSn ?? 0)}
            target="_blank"
            rel="noreferrer"
          >
            {positionName}
          </a>
          {recommendButtonVisible && (
            <Tooltip
              label={<LikeButton isLiked={likeYn} onClick={handleClickLikeButton} />}
              placement="top-left"
              content={
                <>
                  포지션에 <span className={cx('emphasis')}>관심 표현</span>하고, 제안받을 확률 올려보세요!
                </>
              }
              durationForFirstRender={durationForFirstRender}
              className={cx('tooltip')}
              duration={200}
              left={-10}
              iconLeft={17}
              theme="gray"
              condition={!likeYn}
            />
          )}
        </div>
        <div className={cx('infoSubContainer')}>
          <a href={V2Route.getCompanyChannelPage(companySn)} target="_blank" rel="noreferrer">
            {companyName}
          </a>
          {isNeedBadge && (
            <div className={cx('dateInfo')}>
              응답 기한
              <span className={cx('badge', { dDay: dueDateDDay === 0 })}>{decisionDueDateDDayText}</span>
            </div>
          )}
        </div>
      </div>

      <div className={cx('modifiedDate')}>{matchingStatusLastModifiedDateText}</div>

      <div className={cx('matchStatus')}>
        <MatchStatus
          statusType={statusType}
          responseType={responseType}
          onOpenMessage={onOpenMessage}
          positionSn={positionSn ?? 0}
          isOfferClosed={isOfferClosed}
        />
      </div>
    </div>
  );
};

export default MatchListCard;
